import { COINONE_PING, coinoneRequest, diffCoinoneSubscriptions } from './coinone-subscribe';

type WebSocketData = string | ArrayBuffer | Blob;

interface Subscriber {
  id: string;
  codes: string[];
  type: string;
  onMessage: (data: WebSocketData) => void | Promise<void>;
}

interface SocketManagerOptions {
  url: string;
  /** 구독 메시지를 생성하는 함수 */
  buildSubscribeMessage: (type: string, codes: string[]) => unknown;
  /**
   * TRAD-3635 — 'coinone' 이면 buildSubscribeMessage 를 쓰지 않고 워커와 같은 순수 함수(coinone-subscribe.ts)로
   * 종목별 SUBSCRIBE/UNSUBSCRIBE(차이만) + JSON PING 을 보낸다. 생략하면 종전 동작(ticket/객체 + 문자열 PING).
   */
  subscribeFormat?: 'coinone';
  pingInterval?: number;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

let subscriberIdCounter = 0;

/**
 * 거래소별 단일 WebSocket 연결을 관리하는 매니저.
 * 여러 컴포넌트가 subscribe/unsubscribe로 참여하며,
 * 구독자가 0명이면 연결을 종료하고 1명 이상이면 연결을 유지한다.
 */
export class SocketManager {
  private ws: WebSocket | null = null;
  private subscribers = new Map<string, Subscriber>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private isManualDisconnect = false;

  private readonly url: string;
  private readonly buildSubscribeMessage: (type: string, codes: string[]) => unknown;
  private readonly pingInterval: number;
  private readonly reconnectInterval: number;
  private readonly maxReconnectAttempts: number;
  private readonly subscribeFormat: 'coinone' | undefined;
  /** TRAD-3635 — coinone 전용: 지금 연결에 구독된 topic 키(누적 프로토콜이라 차이만 보낸다). 연결마다 비운다. */
  private coinoneSubscribed = new Set<string>();

  constructor(options: SocketManagerOptions) {
    this.subscribeFormat = options.subscribeFormat;
    this.url = options.url;
    this.buildSubscribeMessage = options.buildSubscribeMessage;
    this.pingInterval = options.pingInterval ?? 30_000;
    this.reconnectInterval = options.reconnectInterval ?? 3_000;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
  }

  /**
   * 구독을 추가한다. 첫 구독자가 등록되면 WebSocket 연결을 시작한다.
   * @returns 구독 해제 함수
   */
  subscribe(type: string, codes: string[], onMessage: (data: WebSocketData) => void | Promise<void>): () => void {
    const id = `sub_${++subscriberIdCounter}`;
    this.subscribers.set(id, { id, codes, type, onMessage });

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    } else if (this.ws.readyState === WebSocket.OPEN) {
      this.sendSubscription();
    } else if (this.ws.readyState === WebSocket.CLOSING) {
      // CLOSING 중 새 구독자가 들어온 경우 — 아무 것도 안 하면 이 소켓이 완전히 닫힌 뒤
      // onclose의 재연결 판단(`!isManualDisconnect && code!==1000 && subscribers.size>0`)에
      // 맡겨지는데, 방금 마지막 구독자가 빠져 disconnect()가 부른 CLOSING이었다면
      // isManualDisconnect=true라 재연결이 안 돼 이 새 구독자가 영원히 고아가 된다.
      // 지금 구독자가 있다는 사실 자체가 "수동 종료 의도"를 무효화하므로 되돌린다.
      this.isManualDisconnect = false;
    }

    return () => this.unsubscribe(id);
  }

  private unsubscribe(id: string) {
    this.subscribers.delete(id);

    if (this.subscribers.size === 0) {
      this.disconnect();
    } else if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscription();
    }
  }

  /** 현재 연결 상태 */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** 연결 상태 변경 리스너 */
  private connectionListeners = new Set<(connected: boolean) => void>();

  onConnectionChange(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  /** TRAD-3045 — 재연결을 다 쓰고 포기했을 때 알림. */
  private gaveUpListeners = new Set<() => void>();

  onGaveUp(listener: () => void): () => void {
    this.gaveUpListeners.add(listener);
    return () => this.gaveUpListeners.delete(listener);
  }

  /** TRAD-3045 — 사용자가 "다시 시도"를 눌렀을 때 호출한다. */
  retry() {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.subscribers.size > 0) {
      this.connect();
    }
  }

  private notifyConnectionChange(connected: boolean) {
    this.connectionListeners.forEach(fn => fn(connected));
  }

  private connect() {
    this.isManualDisconnect = false;
    this.cleanup();

    this.ws = new WebSocket(this.url);
    this.coinoneSubscribed = new Set();

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.notifyConnectionChange(true);
      this.startPing();
      this.sendSubscription();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.subscribers.forEach(sub => {
        void sub.onMessage(event.data);
      });
    };

    this.ws.onerror = () => {
      this.notifyConnectionChange(false);
      this.clearPing();
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.notifyConnectionChange(false);
      this.clearPing();

      if (!this.isManualDisconnect && event.code !== 1000 && this.subscribers.size > 0) {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts);
          this.reconnectAttempts += 1;
          this.reconnectTimer = setTimeout(() => this.connect(), delay);
        } else {
          // TRAD-3045 — 재시도를 다 쓰고도 실패 — 조용히 끝내지 않고 알린다.
          this.gaveUpListeners.forEach(fn => fn());
        }
      }
    };
  }

  private disconnect() {
    this.isManualDisconnect = true;
    this.cleanup();
    if (this.ws) {
      this.ws.close(1000);
      this.ws = null;
    }
    this.notifyConnectionChange(false);
  }

  /**
   * 모든 구독자의 codes를 type별로 합쳐서 구독 메시지를 전송한다.
   * - 배열 반환 (Upbit/Bithumb ticket 형식): 하나의 메시지로 합쳐서 전송
   * - 객체 반환 (Binance SUBSCRIBE 형식): type별 개별 메시지로 전송
   */
  private sendSubscription() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const codesByType = new Map<string, Set<string>>();
    this.subscribers.forEach(sub => {
      const existing = codesByType.get(sub.type) ?? new Set();
      sub.codes.forEach(code => existing.add(code));
      codesByType.set(sub.type, existing);
    });

    if (this.subscribeFormat === 'coinone') {
      const { subscribe, unsubscribe, next } = diffCoinoneSubscriptions(this.coinoneSubscribed, codesByType);
      subscribe.forEach(key => this.ws!.send(coinoneRequest('SUBSCRIBE', key)));
      unsubscribe.forEach(key => this.ws!.send(coinoneRequest('UNSUBSCRIBE', key)));
      this.coinoneSubscribed = next;
      return;
    }

    // ticket 패턴 (배열)은 하나의 메시지로 합치고,
    // 비-ticket 패턴 (객체)은 개별 메시지로 전송
    const parts: unknown[] = [];
    let hasTicket = false;

    codesByType.forEach((codes, type) => {
      const message = this.buildSubscribeMessage(type, [...codes]);
      if (Array.isArray(message)) {
        message.forEach((part, i) => {
          if (i === 0 && !hasTicket) {
            parts.push(part);
            hasTicket = true;
          } else if (i > 0) {
            parts.push(part);
          }
        });
      } else {
        // 비-ticket 패턴: 개별 메시지로 즉시 전송 (Binance 등)
        this.ws!.send(JSON.stringify(message));
      }
    });

    if (parts.length > 0) {
      this.ws.send(JSON.stringify(parts));
    }
  }

  private startPing() {
    this.clearPing();
    if (this.pingInterval <= 0) return;
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(this.subscribeFormat === 'coinone' ? COINONE_PING : 'PING');
      }
    }, this.pingInterval);
  }

  private clearPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private cleanup() {
    this.clearPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** 매니저를 완전히 정리한다 (앱 종료 시) */
  destroy() {
    this.subscribers.clear();
    this.connectionListeners.clear();
    this.gaveUpListeners.clear();
    this.disconnect();
  }
}
